import { useState, useEffect, useCallback } from 'react';
import { metabaseService, MetabaseResponse } from '../utils/metabase';

export interface UseMetabaseStatsResult {
    data: MetabaseResponse | null;
    totalANCs: number | null;
    miseEnService: number | null;
    miseEnServiceGPS: number | null;
    preteALivrer: number | null;
    preteAPoser: number | null;
    travauxPoseEffectue: number | null;
    travauxPoseEffectueGPS: number | null;
    statusData: Array<{ count: number | null; status: string }>;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * Hook to fetch Metabase statistics for a specific question
 * @param questionId - The Metabase question ID
 * @param autoFetch - Whether to fetch data automatically on mount (default: true)
 * @returns UseMetabaseStatsResult
 */
export const useMetabaseStats = (questionId: number, autoFetch: boolean = true): UseMetabaseStatsResult => {
    const [data, setData] = useState<MetabaseResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const totalANCs = data ? metabaseService.getTotalANCs(data) : null;
    const statusData = data ? metabaseService.getStatusData(data) : [];

    // Extract "Mise en service" count
    const miseEnService = data
        ? (() => {
              const miseEnServiceRow = statusData.find((item) => item.status === 'Mise en service');
              return miseEnServiceRow ? miseEnServiceRow.count : null;
          })()
        : null;

    // Extract "Prête à livrer" count
    const preteALivrer = data
        ? (() => {
              const preteALivrerRow = statusData.find((item) => item.status === 'Prête à livrer');
              return preteALivrerRow ? preteALivrerRow.count : null;
          })()
        : null;

    // Extract "Prête à poser" count
    const preteAPoser = data
        ? (() => {
              const preteAPoserRow = statusData.find((item) => item.status === 'Prête à poser');
              return preteAPoserRow ? preteAPoserRow.count : null;
          })()
        : null;

    // Extract "Traveaux de pose effectué" count
    const travauxPoseEffectue = data
        ? (() => {
              const travauxPoseEffectueRow = statusData.find((item) => item.status === 'Traveaux de pose effectués');
              return travauxPoseEffectueRow ? travauxPoseEffectueRow.count : null;
          })()
        : null;

    // Extract "MiseEnServiceLatLong" GPS count
    const miseEnServiceGPS = data
        ? (() => {
              const miseEnServiceGPSRow = statusData.find((item) => item.status === 'MiseEnServiceLatLong');
              return miseEnServiceGPSRow ? miseEnServiceGPSRow.count : null;
          })()
        : null;

    // Extract "Traveaux de pose effectués LatLong" GPS count
    const travauxPoseEffectueGPS = data
        ? (() => {
              const travauxPoseEffectueGPSRow = statusData.find((item) => item.status === 'Traveaux de pose effectués LatLong');
              return travauxPoseEffectueGPSRow ? travauxPoseEffectueGPSRow.count : null;
          })()
        : null;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await metabaseService.getQuestionData(questionId);
            setData(response);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
            console.error('Error fetching Metabase stats:', err);
        } finally {
            setLoading(false);
        }
    }, [questionId]);

    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [fetchData, questionId, autoFetch]);

    return {
        data,
        totalANCs,
        miseEnService,
        miseEnServiceGPS,
        preteALivrer,
        preteAPoser,
        travauxPoseEffectue,
        travauxPoseEffectueGPS,
        statusData,
        loading,
        error,
        refetch: fetchData,
    };
};

/**
 * Hook specifically for question 187 (Total ANCs)
 * @param autoFetch - Whether to fetch data automatically on mount (default: true)
 * @returns UseMetabaseStatsResult
 */
export const useTotalANCs = (autoFetch: boolean = true): UseMetabaseStatsResult => {
    return useMetabaseStats(187, autoFetch);
};

export interface UseThresholdDataResult {
    data: {
        maintenance: { [key: string]: number };
        vidange: { [key: string]: number };
    } | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

/**
 * Hook specifically for question 188 (Threshold Data)
 * @param autoFetch - Whether to fetch data automatically on mount (default: true)
 * @returns UseThresholdDataResult
 */
export const useThresholdData = (autoFetch: boolean = true): UseThresholdDataResult => {
    const [data, setData] = useState<{
        maintenance: { [key: string]: number };
        vidange: { [key: string]: number };
    } | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await metabaseService.getThresholdDataFromQuestion188();
            setData(response);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(errorMessage);
            console.error('Error fetching threshold data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [autoFetch]);

    return {
        data,
        loading,
        error,
        refetch: fetchData,
    };
};

export default useMetabaseStats;
